import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, take } from 'rxjs/operators';
import ADD_CROSS from './mutations/add-cross';
import ADD_CROSS_REFERENCE_NOTE from './mutations/add-cross-reference-note';
import ADD_CROSS_REFERENCE_OPINION from './mutations/add-cross-reference-opinion';
import ADD_NO_MATCH from './mutations/add-no-match';
import DELETE_CROSS_REFERENCE from './mutations/delete-cross-reference';
import PROMOTE_CROSS from './mutations/promote-cross';
import GET_CROSS_UPLOAD_REQUESTS from './queries/get-cross-upload-requests';
import UPDATE_CROSS_UPLOAD from './queries/update-cross-upload';
import GET_VIZIENT_REVIEW_OPTIONS from './queries/get-vizient-review-options';
import {
  CrossReferencesOpinionOption,
  CrossReference,
} from '../../types/product';
import GET_CROSS_REFERENCES_OPINION_OPTIONS from './queries/get-cross-references-opinion-options';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})

export class CrossReferenceGraphqlService {
  constructor(private apollo: Apollo) {}

  getCrossUploadRequests(uploadRequestId: number) {
    return this.apollo
      .use('crossreference')
      .watchQuery<{ crossUploadRequests: Array<any> }>({
        query: GET_CROSS_UPLOAD_REQUESTS,
        variables: { uploadRequestId: uploadRequestId },
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.crossUploadRequests),
      );
  }

  getVizientReviewOptions() {
    return this.apollo
      .use('crossreference')
      .watchQuery<{
        vizientReviewOptions: Array<CrossReferencesOpinionOption>;
      }>({
        query: GET_VIZIENT_REVIEW_OPTIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data.vizientReviewOptions),
      );
  }

  updateCrossUpload(
    crossUploadRequestId: number,
    fromProductId: number,
    toProductId?: number,
  ) {
    return this.apollo
      .use('crossreference')
      .watchQuery<{ updateCrossUpload: any }>({
        query: UPDATE_CROSS_UPLOAD,
        variables: {
          crossUploadRequestId: crossUploadRequestId,
          fromProductId: fromProductId,
        },
      })
      .valueChanges.pipe(
        take(1),
        map((request) => request.data),
      );
  }

  addNoMatch(productId: number) {
    return this.apollo
      .use('crossreference')
      .mutate<{ addNoMatch: CrossReference }>({
        variables: { productId },
        mutation: ADD_NO_MATCH,
      });
  }

  promoteCross(fromProductId: number, toProductId: number) {
    return this.apollo
      .use('crossreference')
      .mutate<{ promoteCrossReference: CrossReference }>({
        variables: { fromProductId, toProductId },
        mutation: PROMOTE_CROSS,
      });
  }

  removeCross(fromProductId: number, toProductId: number) {
    return this.apollo
      .use('crossreference')
      .mutate<{ deleteCrossReference: CrossReference }>({
        variables: { fromProductId, toProductId },
        mutation: DELETE_CROSS_REFERENCE,
      });
  }

  addCross(fromProductId: number, toProductId: number) {
    return this.apollo
      .use('crossreference')
      .mutate<{ createCrossReference: CrossReference }>({
        variables: { fromProductId, toProductId },
        mutation: ADD_CROSS,
      });
  }

  addCrossReferenceNote(
    crossReferenceId: number,
    fromProductId: number,
    toProductId: number,
    note: string,
  ) {
    return this.apollo
      .use('crossreference')
      .mutate<{ addCrossReferenceNote: CrossReference }>({
        variables: {
          crossReferenceId,
          fromProductId,
          toProductId,
          note,
        },
        mutation: ADD_CROSS_REFERENCE_NOTE,
      });
  }

  /**
   * @description Update the vote for a crossReference.
   * @param clinicalFeedback takes in an enum key provided by {@link getOpinionOptions}
   */
  addCrossReferenceOpinion(
    crossReferenceId: number,
    fromProductId: number,
    toProductId: number,
    clinicalFeedback: string,
    rfpId?: number,
  ) {
    return this.apollo
      .use('crossreference')
      .mutate<{ addCrossReferenceOpinion: CrossReference }>({
        variables: {
          crossReferenceId,
          fromProductId,
          toProductId,
          clinicalFeedback,
        },
        mutation: ADD_CROSS_REFERENCE_OPINION,
      });
  }
  
  getOpinionOptions(): Observable<CrossReferencesOpinionOption[]> {
    return this.apollo
      .use('crossreference')
      .watchQuery<{ crossReferenceOpinionOptions: Array<CrossReferencesOpinionOption> }>({
        query: GET_CROSS_REFERENCES_OPINION_OPTIONS,
      })
      .valueChanges.pipe(
        take(1),
        map((result) => result.data.crossReferenceOpinionOptions),
      );
  }
}
